import { ReactNode, useState, useEffect } from "react";
import { useClinic } from "@/contexts/ClinicContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Hospital } from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { X, LayoutDashboard, ShieldCheck, User as UserIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocation, useNavigate } from "react-router-dom";

interface Tab {
  id: string;
  title: string;
  component: ReactNode;
}

export function AppLayout({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { clinics, company, selectedClinic, selectedClinicId, setSelectedClinicId, isLoadingClinics } = useClinic();
  const location = useLocation();
  const navigate = useNavigate();

  const [tabs, setTabs] = useState<Tab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [lastClosedId, setLastClosedId] = useState<string | null>(null);

  const titlesMap: Record<string, string> = {
    "/home": "Menu Principal",
    "/schedule": "Agenda",
    "/appointments": "Atendimentos",
    "/patients": "Pacientes",
    "/notifications": "Central de Notificações",
    "/users": "Usuários",
    "/dentists": "Profissionais",
    "/clinics": "Clínicas",
    "/clinics-detail": "Clínicas",
    "/companies": "Empresas",
    "/chat-observer": "Caixa de Mensagens", 
    "/import-appointments": "Importar Atendimentos",
    "/holidays": "Feriados",
    "/insurance-authorizations": "Autorizações",
    "/follow-ups": "Follow-Ups",
  };

  useEffect(() => {
    const id = location.pathname;
    if (id === lastClosedId) return;

    let title = titlesMap[id];
    if (!title && id.startsWith("/prontuario/")) title = "Prontuário";

    setTabs((prev) => {
      const existingTab = prev.find((t) => t.id === id);
      if (existingTab) return prev;

      const newTab = { id, title: title || "Nova Janela", component: children };

      if (prev.length >= 4) {
        return prev.map((tab) => tab.id === activeTabId ? newTab : tab);
      }
      return [...prev, newTab];
    });

    setActiveTabId(id);
  }, [location.pathname, children]);

  const closeTab = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();

    const newTabs = tabs.filter((t) => t.id !== id);
    setTabs(newTabs);
    setLastClosedId(id);

    if (activeTabId === id) {
      if (newTabs.length > 0) {
        const lastTab = newTabs[newTabs.length - 1];
        setActiveTabId(lastTab.id);
        navigate(lastTab.id);
      } else {
        setActiveTabId(null);
        navigate("/home");
      }
    }
  };

  const userInitials = user?.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "U";

  // super_admin (company_id = null) mostra só o logo; demais mostram nome da empresa.
  const isSuperAdmin = !user?.company_id;
  const displayInitials = company?.name
    ? company.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : null;

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="h-14 border-b border-border bg-card flex items-center justify-between px-6 z-20 shadow-soft">
            {/* Empresa / Palavra-chave */}
            <div className="flex items-center gap-3 select-none">
              {isSuperAdmin ? null : company && displayInitials ? (
                <>
                  <div className="h-8 w-8 rounded-md bg-accent/15 flex items-center justify-center">
                    <span className="font-sans text-xs font-semibold text-accent">{displayInitials}</span>
                  </div>
                  <span className="font-sans text-sm font-semibold text-foreground">
                    {company.name}
                  </span>
                </>
              ) : null}
            </div>

            {/* Seletor de Clínica Global - só mostra se não for admin */}
            {user?.company_id && (
              <div className="flex items-center gap-2">
                <Hospital className="h-4 w-4 text-muted-foreground shrink-0" />
                {clinics.length === 1 ? (
                  <span className="font-sans text-sm text-foreground max-w-[240px] truncate">
                    {clinics[0].trade_name}
                  </span>
                ) : clinics.length > 1 ? (
                  <Select value={selectedClinicId ?? ""} onValueChange={setSelectedClinicId}>
                    <SelectTrigger className="h-8 w-[280px] text-sm border-border bg-muted/60 focus:ring-primary">
                      <SelectValue placeholder="Selecione a clínica..." />
                    </SelectTrigger>
                    <SelectContent>
                      {clinics.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.trade_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : isLoadingClinics ? (
                  <span className="font-sans text-xs text-muted-foreground">Carregando…</span>
                ) : null}
              </div>
            )}

            <div className="flex items-center gap-3">
              {/* Badge de role */}
              <span className="hidden sm:inline-flex" title={isSuperAdmin ? "Super Admin" : "Usuário"}>
                {isSuperAdmin
                  ? <ShieldCheck className="h-4 w-4 text-accent" />
                  : <UserIcon className="h-4 w-4 text-muted-foreground" />}
              </span>
              {/* Nome + email */}
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-foreground leading-tight">{user?.name}</p>
                <p className="text-[11px] text-muted-foreground leading-tight">{user?.email}</p>
              </div>
              <Avatar className="h-8 w-8">
                <AvatarFallback className={`font-sans text-xs font-semibold ${isSuperAdmin ? "bg-accent/15 text-accent" : "bg-muted text-muted-foreground"}`}>
                  {userInitials}
                </AvatarFallback>
              </Avatar>
            </div>
          </header>

          {/* Barra de Abas */}
          <div className="flex bg-muted/40 border-b border-border px-3 pt-1.5 gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <div
                key={tab.id}
                onClick={() => {
                  setActiveTabId(tab.id);
                  setLastClosedId(null);
                  navigate(tab.id);
                }}
                className={cn(
                  "group flex items-center gap-2 px-3 py-1.5 font-sans text-xs cursor-pointer rounded-t-md border-t border-x transition-all max-w-[200px] shrink-0",
                  activeTabId === tab.id
                    ? "bg-background border-border text-primary font-medium shadow-soft z-10"
                    : "bg-transparent border-transparent text-muted-foreground hover:bg-muted/70"
                )}
              >
                <span className="truncate uppercase">{tab.title}</span>
                <X
                  size={12}
                  className="opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity shrink-0"
                  onClick={(e) => closeTab(tab.id, e)}
                />
              </div>
            ))}
          </div>

          {/* Conteúdo */}
          <main className="flex-1 relative bg-background overflow-hidden">
            {tabs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground/60">
                <LayoutDashboard size={40} className="mb-3 opacity-30" />
                <p className="font-sans">Escolha uma seção no menu</p>
              </div>
            ) : (
              tabs.map((tab) => (
                <div
                  key={tab.id}
                  className={cn(
                    "absolute inset-0 overflow-auto transition-all duration-200",
                    activeTabId === tab.id ? "block z-10" : "hidden z-0 pointer-events-none"
                  )}
                >
                  {tab.component}
                </div>
              ))
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}